import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from '@one-thd/sui-carousel';
import {
  Card,
  RatingMeter,
  SkeletonBlock,
  SkeletonLine,
  Typography
} from '@one-thd/sui-atomic-components';
import {
  json,
  params,
  string,
  useDataModel,
} from '@thd-nucleus/data-sources';
import { AddToCart } from '@thd-olt-component-react/add-to-cart';
import { ExperienceContext } from '@thd-nucleus/experience-context';

const TOP_SELLER = {
  storeDetails: 'topSeller',
  homeServices: 'homeServicesTopSeller',
  gardenCenter: 'gardenCenterTopSeller',
  rentals: 'rentalsTopSeller',
  proDesk: 'proDeskTopSeller',
};

const EmtCarousel = ({ contentId, pageName, headerTitle, marginClassName }) => {
  const { channel } = useContext(ExperienceContext);
  const [isMounted, setIsMounted] = useState(false);

  const { data, error } = useDataModel('emtContent',
    {
      variables: {
        contentId,
        pageName
      },
      skip: !contentId || !pageName
    }
  );

  const topSellerKey = TOP_SELLER[pageName];
  const items = data?.emtContent?.content?.[topSellerKey]?.products || [];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!items || items?.length === 0 || error) {
    return null;
  }

  const priceElements = (pricing) => {
    const { dollars, cents } = pricing || {};
    if (!dollars && !cents) return null;
    return (
      <div className="sui-font-bold sui-text-3xl sui-leading-none sui-inline-block" style={{ maxWidth: '175px' }}>
        <span className="sui-text-xs sui-align-top">$</span>
        <span>{dollars}</span>
        <span className="sui-sr-only">.</span>
        <span className="sui-text-xs sui-align-top">{cents}</span>
      </div>
    );
  };

  const getImageComponent = ({ index, media, productLabel }) => {
    if (isMounted) {
      return (
        <img
          className="sui-self-center"
          alt={media?.altText || productLabel}
          loading={index > 5 ? 'lazy' : 'eager'}
          src={media?.url}
          width={182}
          height={182}
        />
      );
    }
    return index > 5
      ? <SkeletonBlock aspect="square" height={52} width={52} />
      : (
        <img
          className="sui-self-center"
          alt={media?.altText || productLabel}
          src={media?.url}
          width={182}
          height={182}
        />
      );
  };

  const slides = items.map((item, index) => {
    const {
      canonicalUrl, media, productLabel, pricing, ratingsReviews, brand
    } = item || {};
    const { averageRating, totalReviews } = ratingsReviews || {};
    const displayBrand = brand && brand.toLowerCase() !== 'unbranded' ? brand : '';
    return (
      <Card key={`card-${productLabel}`} grow>
        <div className="sui-flex sui-flex-col sui-grow" style={{ minHeight: '425px' }}>
          <a className="sui-flex sui-flex-col sui-grow sui-mb-3" href={canonicalUrl}>
            {getImageComponent({ index, media, productLabel })}
            <div className="sui-grow sui-flex sui-flex-col sui-justify-between">
              <div className="sui-mt-3">
                {displayBrand && <Typography weight="bold">{displayBrand}</Typography>}
                <Typography lineClamp="4" style={{ maxWidth: '275px' }}>{productLabel}</Typography>
              </div>
              <div>
                {averageRating && totalReviews
                  && (
                    <div className="sui-mt-3">
                      <RatingMeter
                        value={Number(averageRating)}
                        label={Number(averageRating) !== 0 ? `(${totalReviews})` : ''}
                        edge="start"
                        RatingProps={{
                          color: 'brand'
                        }}
                      />
                    </div>
                  )}
                <div className="sui-mt-3">
                  {isMounted
                    ? priceElements(pricing)
                    : <SkeletonLine variant="single" />}
                </div>
              </div>
            </div>
          </a>
          <div>
            <AddToCart
              variant="secondary"
              cartReqParams={{
                itemId: `${item.itemId}`,
                quantity: 1
              }}
              cartOptions={{
                host: 'https://www.homedepot.com',
                channel,
                paypal: false,
                misship: false,
              }}
              onFail={(failErr) => console.warn('Fail!', failErr)}
              onError={(err) => console.error('error!', err)}
            >
              Add To Cart
            </AddToCart>
          </div>
        </div>
      </Card>
    );
  }).filter(Boolean);

  return (
    <div className={`${marginClassName}`} data-component="EmtCarousel">
      <Carousel
        title={<Typography variant="h2" weight="display">{headerTitle}</Typography>}
        disableMargin
        spaceBetween={12}
        hidePaginationOnOneOfOne
        breakpoints={{
          sm: {
            slidesPerView: 2,
            slidesPerGroup: 2
          },
          md: {
            slidesPerView: 4,
            slidesPerGroup: 4
          },
          lg: {
            slidesPerView: 5,
            slidesPerGroup: 5
          },
          xl: {
            slidesPerView: 6,
            slidesPerGroup: 6
          }
        }}
      >
        {slides}
      </Carousel>
    </div>
  );
};

EmtCarousel.propTypes = {
  contentId: PropTypes.string.isRequired,
  pageName: PropTypes.string.isRequired,
  headerTitle: PropTypes.string,
  marginClassName: PropTypes.string
};

EmtCarousel.defaultProps = {
  headerTitle: '',
  marginClassName: ''
};

EmtCarousel.displayName = 'EmtCarousel';

EmtCarousel.dataModel = {
  emtContent: params({
    contentId: string().isRequired(),
    pageName: string().isRequired()
  }).shape({
    content: json()
  })
};

export { EmtCarousel };
